import { useEffect, useState } from 'react';
import { useOp } from '../../api.ts';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { ErrorBox, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import type { SiteDetail } from './types.ts';
import { ConfirmButton } from './ui.tsx';

/** Must match TOKEN_PLACEHOLDER in packages/tenancy/src/edge/guides.ts. */
const TOKEN_PLACEHOLDER = '<YOUR_JC_PROXY_TOKEN>';

interface CheckItem {
  id: string;
  label: string;
  status: 'pass' | 'warn' | 'fail';
  detail: string;
  fix?: string;
}

interface CheckResult {
  ok: boolean;
  checkedAt: string;
  publicUrl: string;
  items: CheckItem[];
}

interface Guide {
  stack: string;
  stackLabel: string;
  title: string;
  intro: string[];
  values: { label: string; value: string }[];
  requirements: { item: string; required: boolean; why: string; ifMissing: string }[];
  snippets: { title: string; language: string; code: string; note?: string }[];
  verify: string[];
}

interface Integration {
  siteRef: string;
  siteName: string;
  type: string;
  applicable: boolean;
  canAdmin: boolean;
  publicHost: string;
  basePath: string;
  publicUrl: string;
  stack: string;
  stacks: { id: string; label: string }[];
  originHostEnabled: boolean;
  originHost: string | null;
  basePathMode: 'kept' | 'stripped';
  countryHeaders: string[];
  defaultCountryHeaders: string[];
  token: { configured: boolean; hint: string | null; rotatedAt: string | null; previousValidUntil: string | null };
  guide: Guide;
  verifiedAt: string | null;
  verifiedBy: string | null;
  lastCheck: CheckResult | null;
  lastCheckedAt: string | null;
}

const STATUS_LABEL: Record<CheckItem['status'], string> = { pass: 'Pass', warn: 'Check', fail: 'Fix' };

async function copy(text: string, toast: ReturnType<typeof useToast>) {
  try {
    await navigator.clipboard.writeText(text);
    toast('success', 'Copied');
  } catch {
    toast('info', 'Select the text and copy it');
  }
}

/**
 * How a partner's reverse proxy reaches this site: stack, origin hostname, base path, country headers, the proxy
 * token (shown once), the generated guide, a shareable guide link and "Verify setup" (docs/partner-integration.md).
 */
export function IntegrationPanel({ site }: { site: SiteDetail }) {
  const toast = useToast();
  const changes = useChanges();
  const [stack, setStack] = useState<string | undefined>(undefined);
  const data = useOp<Integration>('admin.integrations.get', { siteRef: site.ref, ...(stack ? { stack } : {}) }, changes);
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [shared, setShared] = useState<{ url: string; expiresAt: string } | null>(null);
  const [checking, setChecking] = useState(false);
  const [countryDraft, setCountryDraft] = useState('');

  const integration = data.data;
  useEffect(() => {
    if (integration) setCountryDraft(integration.countryHeaders.join(', '));
  }, [integration?.countryHeaders.join(',')]);

  if (data.error) return <ErrorBox error={data.error} onRetry={data.reload} />;
  if (!integration) return <Loading />;
  if (!integration.applicable) {
    return <p className="muted">Embed-only and API-only sites are not served through a partner proxy; use placements and API keys instead.</p>;
  }

  const canAdmin = integration.canAdmin;
  const update = (input: Record<string, unknown>, message: string) => runWrite(toast, 'admin.integrations.update', { siteRef: site.ref, ...input }, message);
  const rotate = async () => {
    const result = await runWrite<{ proxyToken: string; previousValidUntil: string | null }>(toast, 'admin.integrations.rotateToken', { siteRef: site.ref }, 'Proxy token created');
    if (result) setFreshToken(result.proxyToken);
  };
  const share = async () => {
    const result = await runWrite<{ url: string; expiresAt: string }>(toast, 'admin.integrations.shareGuide', { siteRef: site.ref, stack: integration.stack, days: 14 }, 'Guide link created');
    if (result) setShared(result);
  };
  const verify = async () => {
    setChecking(true);
    try {
      await runWrite<{ verified: boolean }>(toast, 'admin.integrations.verify', { siteRef: site.ref }, (r) => (r.verified ? 'Setup verified' : 'Setup check finished: see what to fix'));
    } finally {
      setChecking(false);
    }
  };
  const withToken = (code: string) => (freshToken ? code.split(TOKEN_PLACEHOLDER).join(freshToken) : code);
  const check = integration.lastCheck;

  return (
    <div className="integration">
      <p className="muted small">
        For partners who show this site on their own domain through a CDN or web server. Generate the guide for their stack, send them the link, then run the check.
      </p>

      <div className="integration-status">
        {integration.verifiedAt ? (
          <span className="pill pill-ok">Integration verified {formatDate(integration.verifiedAt)}</span>
        ) : check ? (
          <span className="pill pill-warn">Not verified: last check {formatDate(check.checkedAt)}</span>
        ) : (
          <span className="pill">Not checked yet</span>
        )}{' '}
        <span className="muted small">
          Public URL <code>{integration.publicUrl}</code>
        </span>
      </div>

      <div className="form-grid cols-4">
        <label>
          Partner stack
          <select
            value={integration.stack}
            disabled={!canAdmin}
            onChange={(e) => {
              setStack(e.target.value);
              void update({ stack: e.target.value }, 'Stack saved');
            }}
          >
            {integration.stacks.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-check">
          <input
            type="checkbox"
            checked={integration.originHostEnabled}
            disabled={!canAdmin}
            onChange={(e) => void update({ originHostEnabled: e.target.checked }, e.target.checked ? 'Origin hostname on' : 'Origin hostname off')}
          />
          Own origin hostname
        </label>
        <label>
          Base path {integration.basePath === '/' ? <span className="muted">(whole domain)</span> : <code>{integration.basePath}</code>}
          <select
            value={integration.basePathMode}
            disabled={!canAdmin || integration.basePath === '/'}
            onChange={(e) => void update({ basePathMode: e.target.value }, 'Base path mode saved')}
          >
            <option value="kept">Proxy keeps it in the path</option>
            <option value="stripped">Proxy strips it</option>
          </select>
        </label>
        <label>
          Country headers
          <input
            value={countryDraft}
            disabled={!canAdmin}
            placeholder={`default: ${integration.defaultCountryHeaders.slice(1, 4).join(', ')}, …`}
            onChange={(e) => setCountryDraft(e.target.value)}
            onBlur={() => {
              const next = countryDraft.split(/[\s,]+/).map((h) => h.trim().toLowerCase()).filter(Boolean);
              if (next.join(',') !== integration.countryHeaders.join(',')) void update({ countryHeaders: next }, 'Country headers saved');
            }}
          />
        </label>
      </div>
      {integration.originHost ? (
        <p className="small">
          Origin hostname <code>{integration.originHost}</code>{' '}
          <button type="button" className="btn btn-small" onClick={() => copy(integration.originHost!, toast)}>
            Copy
          </button>
        </p>
      ) : null}

      <section className="card" aria-label="Proxy token">
        <h3 className="section-title">Proxy token</h3>
        {freshToken ? (
          <div className="token-once" role="status">
            <p>
              <strong>Copy it now:</strong> it is shown only this once and stored as a hash. Send it to the partner through a secure channel, never in the guide link.
            </p>
            <code className="token-value">{freshToken}</code>{' '}
            <button type="button" className="btn btn-small" onClick={() => copy(freshToken, toast)}>
              Copy token
            </button>
          </div>
        ) : null}
        <p className="small">
          {integration.token.configured ? (
            <>
              Token <code>{integration.token.hint}</code> created {formatDate(integration.token.rotatedAt)}.
              {integration.token.previousValidUntil ? ` The previous token works until ${formatDate(integration.token.previousValidUntil)}.` : ''}
            </>
          ) : (
            'No token yet. With a token we can tell the partner proxy apart from visitors, without IP allow-lists.'
          )}
        </p>
        {canAdmin ? (
          integration.token.configured ? (
            <ConfirmButton label="Rotate token" className="btn btn-small" title="Create a new proxy token?" confirmLabel="Create new token" onConfirm={rotate}>
              <p>The partner must deploy the new token. The current one keeps working for 72 hours, then requests without the new token are no longer trusted.</p>
            </ConfirmButton>
          ) : (
            <button type="button" className="btn btn-small" onClick={rotate}>
              Create token
            </button>
          )
        ) : null}
      </section>

      <section className="card" aria-label="Integration guide">
        <h3 className="section-title">{integration.guide.title}</h3>
        {integration.guide.intro.map((p) => (
          <p key={p} className="small">
            {p}
          </p>
        ))}
        <div className="table-scroll">
          <table className="table compact" aria-label="What the proxy must do">
            <thead>
              <tr>
                <th scope="col">What</th>
                <th scope="col"></th>
                <th scope="col">If it is missing</th>
              </tr>
            </thead>
            <tbody>
              {integration.guide.requirements.map((r) => (
                <tr key={r.item}>
                  <td title={r.why}>{r.item}</td>
                  <td>{r.required ? <span className="pill pill-warn">Required</span> : <span className="muted small">Recommended</span>}</td>
                  <td className="small">{r.ifMissing}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {integration.guide.snippets.map((s) => (
          <div key={s.title} className="snippet">
            <div className="snippet-head">
              <strong>{s.title}</strong>{' '}
              <button type="button" className="btn btn-small" onClick={() => copy(withToken(s.code), toast)}>
                Copy
              </button>
            </div>
            <pre>
              <code>{withToken(s.code)}</code>
            </pre>
            {s.note ? <p className="muted small">{s.note}</p> : null}
          </div>
        ))}
        {canAdmin ? (
          <div className="row-actions">
            <button type="button" className="btn btn-small" onClick={share}>
              Share guide link
            </button>
            <ConfirmButton
              label="Revoke guide links"
              title="Revoke every shared guide link of this site?"
              confirmLabel="Revoke links"
              onConfirm={() => runWrite(toast, 'admin.integrations.revokeGuideLinks', { siteRef: site.ref }, 'Guide links revoked')}
            >
              <p>Links sent earlier stop working. The partner's set-up is not affected.</p>
            </ConfirmButton>
          </div>
        ) : null}
        {shared ? (
          <p className="small">
            Valid until {formatDate(shared.expiresAt)}, without the token: <code className="token-value">{shared.url}</code>{' '}
            <button type="button" className="btn btn-small" onClick={() => copy(shared.url, toast)}>
              Copy link
            </button>
          </p>
        ) : null}
      </section>

      <section className="card" aria-label="Setup check">
        <h3 className="section-title">Setup check</h3>
        <p className="small muted">Calls {integration.publicUrl} through the partner proxy: diagnostics, a cookie round trip, caching, a page and a test redirect. No personal data and no token values are read.</p>
        {canAdmin ? (
          <button type="button" className="btn btn-primary btn-small" onClick={verify} disabled={checking}>
            {checking ? 'Checking…' : 'Verify setup'}
          </button>
        ) : null}
        {check ? (
          <ul className="checklist" aria-label="Setup checklist">
            {check.items.map((item) => (
              <li key={item.id} className={`check check-${item.status}`}>
                <span className={`pill pill-${item.status === 'pass' ? 'ok' : item.status === 'warn' ? 'warn' : 'danger'}`}>{STATUS_LABEL[item.status]}</span> <strong>{item.label}</strong>
                <span className="small"> {item.detail}</span>
                {item.fix ? <div className="small muted">Fix: {item.fix}</div> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}
